import React from 'react';
import { Link } from 'react-router-dom';
import { Flower2, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { CONTACT, HOURS, SHOP_URL, CATALOG_URL, LOCATIONS } from '../mock';
import { trackCallClick } from '../lib/analytics';

export default function Footer() {
  return (
    <footer id="contact" className="bg-[#26302a] text-[#D9D2C6]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <span className="w-10 h-10 rounded-full bg-[#3D5142] flex items-center justify-center">
                <Flower2 className="w-5 h-5 text-[#E9D4CE]" />
              </span>
              <span className="leading-none">
                <span className="block font-serif-display text-[19px] text-white font-semibold">Delivery Florist</span>
                <span className="block text-[11px] tracking-[0.25em] text-[#9AA593] uppercase">Redwood City</span>
              </span>
            </div>
            <p className="text-[14px] leading-relaxed text-[#A9B0A1]">A real local florist delivering fresh, hand-crafted flowers across the San Francisco Bay Area for over 30 years.</p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif-display text-white text-[18px] mb-4">Get in Touch</h4>
            <ul className="space-y-3 text-[14px]">
              <li className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#D9A79C] shrink-0" /> {CONTACT.address}, {CONTACT.cityLine}</li>
              <li><a href={CONTACT.phoneHref} onClick={() => trackCallClick('footer')} className="flex gap-3 hover:text-white transition-colors"><Phone className="w-4 h-4 mt-0.5 text-[#D9A79C]" /> {CONTACT.phone}</a></li>
              <li><a href={`mailto:${CONTACT.email}`} className="flex gap-3 hover:text-white transition-colors"><Mail className="w-4 h-4 mt-0.5 text-[#D9A79C]" /> {CONTACT.email}</a></li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-serif-display text-white text-[18px] mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#D9A79C]" /> Hours</h4>
            <ul className="space-y-2 text-[14px]">
              {HOURS.map((h) => (
                <li key={h.day} className="flex justify-between gap-4"><span className="text-[#A9B0A1]">{h.day}</span><span>{h.time}</span></li>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <h4 className="font-serif-display text-white text-[18px] mb-4">Order Flowers</h4>
            <p className="text-[14px] text-[#A9B0A1] mb-4">Ready to send a little joy? Browse and order online now.</p>
            <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-full bg-[#C4715A] hover:bg-[#a95a45] text-white text-[14px] font-medium px-6 py-3 transition-colors">Shop Now</a>
            <a href={CATALOG_URL} target="_blank" rel="noopener noreferrer" className="block mt-3 text-[14px] text-[#A9B0A1] hover:text-white transition-colors underline underline-offset-4">View full catalog</a>
          </div>

          {/* Same-Day Delivery Areas */}
          <div>
            <h4 className="font-serif-display text-white text-[18px] mb-4">Same-Day Delivery Areas</h4>
            <ul className="space-y-2.5 text-[14px]">
              {LOCATIONS.map((l) => (
                <li key={l.slug}>
                  <Link to={`/flower-delivery/${l.slug}`} className="text-[#A9B0A1] hover:text-white transition-colors">
                    Flowers in {l.city} ({l.zip})
                  </Link>
                </li>
              ))}
              <li><Link to="/blog" className="text-[#A9B0A1] hover:text-white transition-colors underline underline-offset-4">Flower delivery blog</Link></li>
              <li><Link to="/seasonal-flowers" data-testid="footer-seasonal-link" className="text-[#A9B0A1] hover:text-white transition-colors underline underline-offset-4">Seasonal flower calendar</Link></li>
              <li><Link to="/birth-flowers" data-testid="footer-birth-flowers-link" className="text-[#A9B0A1] hover:text-white transition-colors underline underline-offset-4">Birth flowers by month</Link></li>
              <li><Link to="/blog/how-to-make-flowers-last-longer" data-testid="footer-flower-care-link" className="text-[#A9B0A1] hover:text-white transition-colors underline underline-offset-4">Flower care tips</Link></li>
              <li><Link to="/blog/corporate-office-flowers-silicon-valley" data-testid="footer-corporate-flowers-link" className="text-[#A9B0A1] hover:text-white transition-colors underline underline-offset-4">Office &amp; corporate flowers</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[13px] text-[#8E958668]">
          <span className="text-[#8E9586]">© {new Date().getFullYear()} Delivery Florist | Redwood City. All rights reserved.</span>
          <div className="flex gap-5 text-[#8E9586]">
            <a href="#top" className="hover:text-white transition-colors">FAQs</a>
            <a href="#top" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="#top" className="hover:text-white transition-colors">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
